import React from "react";

import { useVirtualizer } from "@tanstack/react-virtual";

import { LoadingIndicator, Modal, ScrollArea, SearchBar, useManualModalState } from "@synthesis/shared-ui";
import { cn, logger } from "@synthesis/shared-utils";

import { type SnippetItem, isIgnorableSearchQuery, useHelpSearch } from "../search/use-help-search";

const SEARCH_QUERY_STORAGE_KEY = "helpPageModalSearchQuery";
const SEARCH_SCROLL_ANCHOR_KEY = "helpPageModalSearchScrollAnchorId";

type HelpSection = {
  title: string;
  subsections?: { title: string; file: string }[];
};

type ModalSearchProps = {
  sectionsData: HelpSection[];
  handleItemClick: (itemTitle: string, filePath: string, anchorId?: string, position?: number, query?: string) => Promise<void>;
  trigger: React.ReactNode;
};

const buildTitleMatchSnippet = (title: string, query: string) => {
  const normalizedQuery = query.toLowerCase();
  const matchIndex = title.toLowerCase().indexOf(normalizedQuery);

  if (matchIndex === -1) {
    return { snippet: title, matchStart: 0, matchLength: 0 };
  }

  return {
    snippet: title,
    matchStart: matchIndex,
    matchLength: query.length,
  };
};

const renderSnippetHighlight = (item: SnippetItem) => {
  if (item.matchStart === undefined || item.matchLength === undefined || item.matchLength === 0) {
    return item.snippet;
  }

  const before = item.snippet.slice(0, item.matchStart);
  const match = item.snippet.slice(item.matchStart, item.matchStart + item.matchLength);
  const after = item.snippet.slice(item.matchStart + item.matchLength);

  return (
    <>
      {before}
      <mark id={item.anchorId} className="bg-tertiary-95">
        {match}
      </mark>
      {after}
    </>
  );
};

export const ModalSearch = ({ trigger, ...contentProps }: ModalSearchProps) => {
  return (
    <Modal.Root manual trigger={trigger as React.ReactElement}>
      <ModalSearchContent {...contentProps} trigger={trigger} />
    </Modal.Root>
  );
};

const ModalSearchContent = ({ sectionsData, handleItemClick }: ModalSearchProps) => {
  const { closeModal } = useManualModalState();
  const searchValueRef = React.useRef("");
  const [searchQuery, setSearchQuery] = React.useState(() => sessionStorage.getItem(SEARCH_QUERY_STORAGE_KEY) || "");
  const parentRef = React.useRef<HTMLDivElement>(null);
  const scrollRestoredRef = React.useRef(false);

  const allMdUrls = React.useMemo(() => {
    return sectionsData.flatMap((section) =>
      section.subsections ? section.subsections.filter((sub) => sub.file).map((sub) => `/help/${sub.file}`) : [],
    );
  }, [sectionsData]);

  const { results, search, ready, isSearching } = useHelpSearch(allMdUrls);
  const isSearchLoading = Boolean(searchQuery.trim()) && (!ready || isSearching);

  React.useEffect(() => {
    if (!ready) return;

    const savedQuery = sessionStorage.getItem(SEARCH_QUERY_STORAGE_KEY) || "";
    searchValueRef.current = savedQuery;

    if (savedQuery) {
      search(savedQuery);
    }
  }, [ready, search]);

  const allResults = React.useMemo(() => {
    const query = searchQuery.trim();
    if (!query || isIgnorableSearchQuery(query)) return [];

    const normalizedQuery = query.toLowerCase();
    const workerResults = results.query === query ? results.items : [];
    const titleMatches: SnippetItem[] = sectionsData
      .flatMap((section) => section.subsections || [])
      .filter((subsection) => subsection.file && subsection.title?.toLowerCase().includes(normalizedQuery))
      .map((subsection) => {
        const url = `/help/${subsection.file}`;
        const anchorId = `search-title-${url.replace(/[^a-z0-9]/gi, "_")}`;
        const titleMatch = buildTitleMatchSnippet(subsection.title, query);

        return {
          url,
          snippet: titleMatch.snippet,
          matchStart: titleMatch.matchStart,
          matchLength: titleMatch.matchLength,
          anchorId,
          isTitleMatch: true,
        };
      });

    return [...titleMatches, ...workerResults];
  }, [results, searchQuery, sectionsData]);

  const handleSearchResultClick = async (item: SnippetItem, query: string) => {
    sessionStorage.setItem(SEARCH_SCROLL_ANCHOR_KEY, item.anchorId);

    const filePath = item.url.replace(/^\/help\//, "");
    const section = sectionsData.flatMap((s) => s.subsections || []).find((sub) => `/help/${sub.file}` === item.url);
    const title = section?.title || filePath;

    if (item.isTitleMatch || item.position === undefined) {
      await handleItemClick(title, filePath);
    } else if (item.isHeadingMatch && item.heading) {
      await handleItemClick(title, filePath);

      setTimeout(() => {
        const element = document.getElementById(item.heading!);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          logger.error("Подраздел не найден через 500 мс:", item.heading);
        }
      }, 500);
    } else {
      await handleItemClick(title, filePath, item.anchorId, item.position, query);

      setTimeout(() => {
        const element = document.getElementById(item.anchorId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          logger.error("Якорь всё ещё не найден через 100 мс:", item.anchorId);
        }
      }, 500);
    }

    closeModal();
  };

  const groupedResults = React.useMemo(() => {
    if (!allResults.length) return [];

    const map = new Map<string, { sectionTitle: string; items: SnippetItem[] }>();

    allResults.forEach((item) => {
      const section = sectionsData.flatMap((sectionData) => sectionData.subsections || []).find((sub) => `/help/${sub.file}` === item.url);
      const sectionTitle = section?.title || item.url;

      if (!map.has(item.url)) {
        map.set(item.url, { sectionTitle, items: [] });
      }

      map.get(item.url)!.items.push(item);
    });

    return Array.from(map.values());
  }, [allResults, sectionsData]);

  const flatItems = groupedResults.flatMap((group) => {
    const groupItems = group.items.map((item, index) => ({
      type: "item" as const,
      item,
      isLastInGroup: index === group.items.length - 1,
    }));

    return [
      {
        type: "group" as const,
        sectionTitle: group.sectionTitle,
      },
      ...groupItems,
    ];
  });

  const rowVirtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 100,
    overscan: 8,
  });

  React.useEffect(() => {
    if (scrollRestoredRef.current || flatItems.length === 0) return;

    const savedAnchorId = sessionStorage.getItem(SEARCH_SCROLL_ANCHOR_KEY);
    if (!savedAnchorId) return;

    const targetIndex = flatItems.findIndex((flatItem) => flatItem.type === "item" && flatItem.item.anchorId === savedAnchorId);

    if (targetIndex === -1) return;

    scrollRestoredRef.current = true;

    const scrollToTarget = () => {
      rowVirtualizer.scrollToIndex(targetIndex, { align: "center" });
    };

    requestAnimationFrame(scrollToTarget);
    setTimeout(scrollToTarget, 100);
  }, [flatItems, rowVirtualizer]);

  const handleSearch = (value: string) => {
    search(value);
    setSearchQuery(value);
    searchValueRef.current = value;
    sessionStorage.setItem(SEARCH_QUERY_STORAGE_KEY, value);
  };

  return (
    <Modal.Content className="h-[80vh] w-[900px]">
      <Modal.Title>Руководство Пользователя</Modal.Title>
      <Modal.Description className="p-8">
        <div>
          <SearchBar className="mb-6" clearable defaultValue={searchQuery} onChange={handleSearch} debounceTime={250} autoFocus />
          <ScrollArea ref={parentRef} className="flex max-h-[60vh] w-full flex-col rounded-sm bg-white px-3 py-2">
            {isSearchLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingIndicator />
              </div>
            ) : (
              <div
                style={{
                  height: rowVirtualizer.getTotalSize(),
                  position: "relative",
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const item = flatItems[virtualRow.index];
                  if (!item) return null;

                  return (
                    <div
                      key={virtualRow.key}
                      ref={rowVirtualizer.measureElement}
                      data-index={virtualRow.index}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      {item.type === "group" ? (
                        <div className="border-neutrals-90 text-neutrals-20 heading-h4 border-b-2 px-3 py-2">{item.sectionTitle}</div>
                      ) : (
                        <div
                          onClick={() => handleSearchResultClick(item.item, searchValueRef.current)}
                          className={cn("hover:bg-neutrals-99 cursor-pointer rounded-sm px-3 py-2", !item.isLastInGroup && "border-b-2")}
                        >
                          {item.item.heading && <div className="text-neutrals-50 subtitle-sm mb-1">{item.item.heading}</div>}
                          {item.item.isTableMatch && <div className="text-neutrals-50 caption-sm mb-1">Результат из таблицы</div>}
                          <div className="text-type-common subtitle-base">{renderSnippetHighlight(item.item)}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      </Modal.Description>
    </Modal.Content>
  );
};
